import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Rating,
  useTheme,
  useMediaQuery,
  Skeleton,
} from '@mui/material';
import { Star as StarIcon } from '@mui/icons-material';

interface Testimonial {
  id: string;
  name: string;
  role: string;
  avatar?: string;
  rating: number;
  text: string;
  location: string;
}

interface TestimonialsSectionProps {
  testimonials: Testimonial[];
  loading: boolean;
}

export const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({ 
  testimonials, 
  loading 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Default testimonials if none are loaded
  const defaultTestimonials: Testimonial[] = [
    {
      id: '1',
      name: 'Sarah Johnson',
      role: 'Patient',
      rating: 5,
      text: 'Careva made it so easy to find a physical therapist who could help me recover from my knee surgery. The caregiver was professional, knowledgeable, and really cared about my progress.',
      location: 'San Francisco, CA',
    },
    {
      id: '2',
      name: 'Michael Chen',
      role: 'Patient',
      rating: 5,
      text: 'I needed occupational therapy after my stroke, and Careva connected me with an amazing therapist. The booking process was simple, and the care I received was exceptional.',
      location: 'Austin, TX',
    },
    {
      id: '3',
      name: 'Emily Rodriguez',
      role: 'Caregiver',
      rating: 5,
      text: 'As a physical therapist, Careva has given me the flexibility to work with patients on my own schedule. The platform is user-friendly and the patients are wonderful to work with.',
      location: 'Miami, FL',
    },
    {
      id: '4',
      name: 'David Thompson',
      role: 'Patient',
      rating: 5,
      text: 'Finding quality senior care for my mother was challenging until I discovered Careva. The caregivers are all background-checked and truly compassionate professionals.',
      location: 'Seattle, WA',
    },
    {
      id: '5',
      name: 'Lisa Park',
      role: 'Caregiver',
      rating: 5,
      text: 'Careva has transformed how I provide home health services. The platform makes scheduling easy and helps me build lasting relationships with my patients.',
      location: 'Denver, CO',
    },
  ];

  const displayTestimonials = testimonials.length > 0 ? testimonials : defaultTestimonials;

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase();
  };

  return (
    <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: theme.palette.grey[50] }}>
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 'bold',
              color: theme.palette.text.primary,
              mb: 2,
            }}
          >
            What Our Users Say
          </Typography>
          <Typography
            variant="h6"
            component="p"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Real stories from patients and caregivers who have experienced 
            the quality and convenience of Careva.
          </Typography>
        </Box>

        {/* Testimonials Grid */}
        <Grid container spacing={4}>
          {displayTestimonials.map((testimonial, index) => (
            <Grid item xs={12} md={6} lg={4} key={testimonial.id}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    elevation: 8,
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardContent sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {/* Rating */}
                  {loading ? (
                    <Skeleton variant="rectangular" width={120} height={24} sx={{ mb: 2 }} />
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      <Rating
                        value={testimonial.rating}
                        readOnly
                        size="small"
                        icon={<StarIcon fontSize="inherit" />}
                        sx={{
                          '& .MuiRating-iconFilled': {
                            color: theme.palette.warning.main,
                          },
                        }}
                      />
                    </Box>
                  )}

                  {/* Testimonial Text */}
                  {loading ? (
                    <>
                      <Skeleton variant="text" height={60} sx={{ mb: 1 }} />
                      <Skeleton variant="text" height={60} sx={{ mb: 1 }} />
                      <Skeleton variant="text" height={40} />
                    </>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3, flexGrow: 1 }}>
                      <Typography
                        variant="h4"
                        sx={{
                          color: theme.palette.primary.main,
                          fontWeight: 'bold',
                          fontSize: '2rem',
                          lineHeight: 1,
                          mr: 1,
                          mt: -0.5,
                        }}
                      >
                        "
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: theme.palette.text.primary,
                          lineHeight: 1.6,
                          fontStyle: 'italic',
                          flex: 1,
                        }}
                      >
                        {testimonial.text}
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          color: theme.palette.primary.main,
                          fontWeight: 'bold',
                          fontSize: '2rem',
                          lineHeight: 1,
                          ml: 1,
                          mt: -0.5,
                        }}
                      >
                        "
                      </Typography>
                    </Box>
                  )}

                  {/* User Info */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                    {loading ? (
                      <>
                        <Skeleton variant="circular" width={48} height={48} sx={{ mr: 2 }} />
                        <Box>
                          <Skeleton variant="text" width={120} height={20} sx={{ mb: 0.5 }} />
                          <Skeleton variant="text" width={100} height={16} />
                        </Box>
                      </>
                    ) : (
                      <>
                        <Avatar
                          src={testimonial.avatar}
                          sx={{
                            width: 48,
                            height: 48,
                            mr: 2,
                            backgroundColor: theme.palette.primary.main,
                            fontSize: '1.2rem',
                            fontWeight: 'bold',
                          }}
                        >
                          {!testimonial.avatar && getInitials(testimonial.name)}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 'bold',
                              color: theme.palette.text.primary,
                              mb: 0.5,
                            }}
                          >
                            {testimonial.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {testimonial.role} • {testimonial.location}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Bottom CTA */}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              mb: 3,
            }}
          >
            Ready to experience quality care?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 'bold',
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            Join thousands of satisfied users →
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};
